import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { GridColDef, GridMoreVertIcon, GridRowParams } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useState } from "react";
import AddIcon from "../../src/assets/AddIcon.svg";
import { getZonesByRIOId, removeZoneFromUser } from "../api/requests/zones";
import CommDataGrid from "../components/CommDataGrid";
import DeleteDialog from "../components/common/DeleteDialog";
import CreateOrEditZone from "../components/CreateOrEditZone";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { commonStrings } from "../static/commonStrings";
import { ZoneProps, ZoneType } from "../utils/types";
import { menuItemStyles } from "./Groups";
import { containedButtonWithIconSx } from "./Users";
import AssemblyConstituency from "./AssemblyConstituency";

const Zone = ({ selectedRIO }: ZoneProps) => {
  const [zones, setZones] = useState<ZoneType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [searchText, setSearchText] = useState<string>("");
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [selectedRow, setSelectedRow] = useState<ZoneType | null>(null);
  const [isEditDialog, setIsEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);

  const fetchZones = async () => {
    setLoading(true);
    const zoneRes = await getZonesByRIOId(selectedRIO.rioId);
    const formattedData = zoneRes.data.map((zone: ZoneType) => ({
      id: zone.zoneId,
      ...zone,
    }));
    setZones(formattedData);
    setTotalRowsCount(formattedData.length || 0);
    setLoading(false);
  };

  useEffect(() => {
    fetchZones();
    setSelectedRow(null);
  }, [selectedRIO.rioId]);
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedRow) {
      handleMenuClose();
    }
    setOpenCreateDialog(true);
    setIsEditDialog(true);
  };
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: ZoneType
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleSearch = useCallback((value: string) => {
    setSearchText(value.trim());
  }, []);

  const handleSaveZone = async () => {
    closeCreateDialog();
    fetchZones();
  };

  const closeCreateDialog = () => {
    setOpenCreateDialog(false);
    setOpenDeleteDialog(false);
    setIsEditDialog(false);
  };

  const createZone = () => {
    setOpenCreateDialog(true);
  };

  const buttonsComponent = useCallback(
    () => (
      <>
        {user?.permissions && (
          <>
            {user.permissions.some(
              (p) => p.module === "Users" && p.actions.includes("Create")
            ) ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={createZone}
                  sx={containedButtonWithIconSx}
                >
                  <img src={AddIcon} /> Add Zone
                </Button>
              </Box>
            ) : null}
          </>
        )}
      </>
    ),
    [user]
  );

  const handleDelete = async () => {
    if (!selectedRow) {
      showAlert("No Zone selected", "error");
      return;
    }
    try {
      const removeZoneRes = await removeZoneFromUser(selectedRow.zoneId);
      if (removeZoneRes.status == 200) {
        showAlert("Deleted successfully", "success");
        setSelectedRow(null);
        handleMenuClose();
        closeCreateDialog();
        fetchZones();
      }
    } catch (error: any) {
      showAlert("errorMessage", "error");
      setSelectedRow(null);
    }
    handleMenuClose();
  };

  const handleOpenZoneTable = (row: GridRowParams<ZoneType>) => {
    setSelectedRow(row.row);
  };

  const filteredList = zones.filter((zone) =>
    zone.zoneName.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: "zoneName",
      headerName: "Zone Name",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        return (
          <Typography key={row.id} variant="body2" sx={{ display: "contents" }}>
            {row.zoneName || "--"}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <>
          {params.row.status !== "Deleted" && (
            <>
              <IconButton
                onClick={(event) => {
                  handleMenuOpen(event, params.row);
                }}
              >
                <GridMoreVertIcon sx={{ color: "#64748B" }} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={menuItemStyles}
              >
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) => p.module === "Users" && p.actions.includes("Edit")
                    ) ? (
                      <MenuItem onClick={handleEdit}>Edit</MenuItem>
                    ) : null}
                  </>
                )}{" "}
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) =>
                        p.module === "Users" && p.actions.includes("Delete")
                    ) ? (
                      <MenuItem
                        onClick={() => selectedRow && setOpenDeleteDialog(true)}
                      >
                        Delete
                      </MenuItem>
                    ) : null}
                  </>
                )}{" "}
              </Menu>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
      <Box sx={{ flex: 1, maxWidth: selectedRow ? "50%" : "100%" }}>
        <CommDataGrid
          title={`Zones of ${selectedRIO.rioName}`}
          description="View and manage Zones linked to this RIO"
          rows={filteredList}
          columns={columns}
          section="Zones"
          loading={loading}
          totalRowsCount={totalRowsCount}
          searchText={searchText}
          handleSearch={handleSearch}
          renderButtons={buttonsComponent}
          onRowClick={handleOpenZoneTable}
        />
        {openCreateDialog ? (
          <CreateOrEditZone
            onSaveZoneCB={handleSaveZone}
            setOpenCreateDialog={closeCreateDialog}
            isEdit={isEditDialog}
            zoneTypeData={selectedRow!}
            initialSelectedRIO={selectedRIO}
          />
        ) : null}
        {openDeleteDialog ? (
          <DeleteDialog
            actionButton={handleDelete}
            cancelButton={() => setOpenDeleteDialog(!openDeleteDialog)}
            dialogSubTitle={`Zone ${selectedRow?.zoneName} will be deleted, if you click on 'Yes'`}
            dialogTextTitle={`Are you sure want to delete ${selectedRow?.zoneName}?`}
            title={`Delete ${commonStrings.sideBarMenuTitles.zone}`}
          />
        ) : null}
      </Box>
      {selectedRow ? (
        <Box sx={{ flex: 1, maxWidth: "50%" }}>
          <AssemblyConstituency selectedZone={selectedRow} />{" "}
        </Box>
      ) : null}
    </Box>
  );
};

export default Zone;
